/**
 * Type-safe localStorage wrapper with optional validation
 */
export class TypedStorage<T> {
  constructor(
    private readonly key: string,
    private readonly validate?: (data: unknown) => data is T
  ) {}

  /**
   * Get value from localStorage
   * Returns null if not found, parse fails, or validation fails
   */
  get(): T | null {
    try {
      const stored = localStorage.getItem(this.key);
      if (!stored) return null;

      const parsed: unknown = JSON.parse(stored);

      if (this.validate && !this.validate(parsed)) {
        console.warn(`Invalid data in localStorage key "${this.key}"`);
        return null;
      }

      return parsed as T;
    } catch (e) {
      console.error(`Failed to parse ${this.key} from localStorage:`, e);
      return null;
    }
  }

  /**
   * Save value to localStorage
   * Returns true on success, false on failure
   */
  set(value: T): boolean {
    try {
      localStorage.setItem(this.key, JSON.stringify(value));
      return true;
    } catch (e) {
      console.error(`Failed to save ${this.key} to localStorage:`, e);
      return false;
    }
  }

  /**
   * Remove value from localStorage
   */
  clear(): void {
    localStorage.removeItem(this.key);
  }

  /**
   * Get value or return default if not found
   */
  getOrDefault(defaultValue: T): T {
    return this.get() ?? defaultValue;
  }

  /**
   * Update a specific property (for object storage)
   * Works only for objects, merges the update into existing data
   */
  update(updates: Partial<T>): boolean {
    const current = this.getOrDefault({} as T);
    return this.set({ ...current, ...updates });
  }
}
